/**
 * Ce que l'abonnement ouvre, et ce qui reste gratuit (docs/01 §6, docs/06 §3).
 *
 * Le modèle est un **freemium d'activation**, pas un freemium de démonstration : le
 * gratuit doit suffire au parent invité pour rejoindre et rester, sans quoi il ne
 * rejoindra pas — et le produit se vend à deux personnes ou à personne.
 *
 * Deux décisions méritent d'être écrites, parce qu'elles ne relèvent pas du prix :
 *
 * 1. **Les alertes sont gratuites.** Elles servent à prévenir l'autre parent qu'un enfant
 *    est malade ou qu'un passage de bras est retardé. Les placer derrière un paiement
 *    ferait dépendre la sécurité d'un enfant de l'état d'un abonnement. Ce n'est pas
 *    négociable, quel qu'en soit le coût commercial.
 * 2. **La lecture reste toujours ouverte.** Un abonnement qui expire ne doit jamais fermer
 *    l'accès à ce qui a déjà été écrit : l'historique appartient aux deux parents, pas au
 *    payeur. On cesse de pouvoir écrire, jamais de pouvoir lire.
 */
export const FONCTIONS = [
    'calendrier',
    'messagerie',
    'alertes',
    'journal',
    'depenses',
    'documents',
    'exports',
];
/**
 * Fonctions ouvertes sans abonnement.
 *
 * La messagerie en faisait partie, plafonnée à trente messages par mois. Elle est passée
 * au payant : l'écriture d'un message relève désormais de l'abonnement, comme le journal
 * ou les dépenses. Le calendrier et les alertes restent gratuits — le premier pour que le
 * parent invité rejoigne sans obstacle, les secondes parce que faire dépendre la sécurité
 * d'un enfant de l'état d'un abonnement n'est pas négociable.
 */
export const FONCTIONS_GRATUITES = ['calendrier', 'alertes'];
/** Statuts qui ouvrent les fonctions payantes. */
const STATUTS_OUVRANTS = new Set(['trialing', 'active']);
/**
 * L'abonnement ouvre-t-il les fonctions payantes à cet instant ?
 *
 * `past_due` n'ouvre plus : un paiement en échec laisse l'écriture ouverte jusqu'à la fin
 * de la période déjà réglée, et c'est cette date qui tranche — pas le statut seul.
 */
export function abonnementActif(etat, maintenant) {
    if (!STATUTS_OUVRANTS.has(etat.statut))
        return false;
    if (etat.jusquau === null)
        return true;
    return etat.jusquau.getTime() > maintenant.getTime();
}
export function droitsDe(etat, maintenant) {
    const abonne = abonnementActif(etat, maintenant);
    return { ecriture: abonne ? FONCTIONS : FONCTIONS_GRATUITES, abonne };
}
/** Cette fonction est-elle ouverte en écriture ? */
export function peutEcrire(droits, fonction) {
    return droits.ecriture.includes(fonction);
}
/**
 * Message affiché quand une fonction demande un abonnement.
 *
 * Factuel, jamais culpabilisant, et il ne met **jamais** l'autre parent en cause : le
 * paiement est individuel, et suggérer que l'autre pourrait payer transformerait
 * l'abonnement en sujet de négociation entre eux.
 */
export function motifIndisponible(fonction) {
    const libelles = {
        calendrier: 'Le calendrier',
        messagerie: 'La messagerie',
        alertes: 'Les alertes',
        journal: 'Le journal',
        depenses: 'Les dépenses partagées',
        documents: 'Les documents',
        exports: "L'export de dossier",
    };
    return `${libelles[fonction]} fait partie de l'abonnement. Votre accès à ce qui est déjà enregistré reste entier.`;
}
/**
 * Codes d'erreur du domaine facturation.
 *
 * Ils existent pour que l'interface distingue « il faut s'abonner » de « cette action est
 * impossible » — deux refus qui partagent le même statut HTTP et appellent des réactions
 * opposées. Comparer des phrases françaises casserait à la première reformulation.
 */
export const CODE_ABONNEMENT_REQUIS = 'SUBSCRIPTION_REQUIRED';
/**
 * Prix de l'abonnement, en **centimes** (docs/06 §1).
 *
 * En centimes comme tout montant du produit : un prix en flottant finit par s'afficher
 * « 9.9 € » quelque part. Ici plutôt que dans l'interface, parce que trois surfaces
 * l'affichent — la vitrine, l'invitation à souscrire, l'écran d'abonnement — et qu'un prix
 * recopié trois fois finit par diverger, au mauvais endroit.
 *
 * Le montant fait foi côté Stripe, pas ici : cette constante sert à **annoncer** le prix,
 * jamais à encaisser. Une divergence entre les deux se voit à l'écran ; l'inverse serait
 * une facturation surprise.
 */
export const PRIX_MENSUEL_CENTIMES = 990;
/**
 * Prix annuel, en centimes : deux mois offerts sur douze.
 *
 * **À confirmer par Jacques.** `docs/06` fixe les 9,90 €/mois face aux 19,90 € du
 * concurrent ; il ne dit rien de l'annuel. « Deux mois offerts » est la remise usuelle et
 * elle se calcule de tête — un parent vérifie en une seconde qu'il ne se fait pas avoir,
 * ce qui compte plus que quelques euros dans un produit qui vend la confiance.
 */
export const PRIX_ANNUEL_CENTIMES = PRIX_MENSUEL_CENTIMES * 10;
/**
 * Portée de l'abonnement : pour soi, ou pour les deux parents du dossier.
 *
 * Le `duo` n'est pas une remise de volume, c'est un **geste**. Il existe parce que la
 * situation réelle est fréquente : l'un des deux parents a les moyens de payer, l'autre
 * non, et le dossier ne vaut que si les deux peuvent y écrire — un historique où un seul
 * parent consigne n'est plus un historique partagé, c'est un dossier à charge.
 *
 * Il est encadré par trois règles, portées par le code et non par une promesse :
 *
 * 1. **Le parent couvert n'est pas informé de qui paie.** Il voit que l'écriture est
 *    ouverte, pas à qui il le doit. Un « c'est moi qui paie » a sa place dans une
 *    conversation entre adultes, jamais dans une interface qui le rappellerait à chaque
 *    écran (principe anti-conflit n°6).
 * 2. **Il peut prendre le sien à tout moment**, et cesse alors de dépendre de personne.
 * 3. **Ce qui a été payé va au bout de sa période.** Une résiliation ne referme pas
 *    l'écriture de l'autre du jour au lendemain : l'accès se retire comme il est venu,
 *    à l'échéance.
 */
export const PORTEES_ABONNEMENT = ['seul', 'duo'];
/**
 * Les quatre formules, telles qu'elles se nomment à l'écran.
 *
 * Elles remplacent « version gratuite » et « version parent », qui décrivaient un état du
 * compte plutôt qu'une offre : un parent qui lit « version parent » ne sait pas s'il y a
 * autre chose à côté, ni ce qu'il choisit. Quatre noms courts s'annoncent, se comparent et
 * se retiennent — ce que trois périphrases ne faisaient pas.
 *
 * - **Link** — gratuit, sans date limite. Le calendrier de garde et les alertes. C'est ce
 *   qui permet au parent invité de rejoindre et de rester : le produit se vend à deux
 *   personnes ou à personne (voir `FONCTIONS_GRATUITES`).
 * - **Solo** — l'écriture ouverte, pour un parent.
 * - **Duo** — la même chose, payée une fois pour les deux parents. Ce n'est pas un palier
 *   au-dessus de Solo : c'est Solo, pris pour deux (voir `PORTEES_ABONNEMENT`).
 * - **Pro** — l'accès en lecture d'un avocat, d'un médiateur ou d'un travailleur social,
 *   ouvert par un parent. Gratuit, et jamais vendu à un parent.
 *
 * Les noms ne se traduisent pas : ce sont ceux de la grille tarifaire publique, identiques
 * dans les six langues du catalogue. Les traduire ferait deux offres d'une seule.
 */
export const FORMULES = ['link', 'solo', 'duo', 'pro'];
export const NOM_FORMULE = {
    link: 'Link',
    solo: 'Solo',
    duo: 'Duo',
    pro: 'Pro',
};
/** La formule que désigne une portée d'abonnement. */
export function formuleDePortee(portee) {
    return portee === 'duo' ? 'duo' : 'solo';
}
/**
 * Prix de la portée « les deux parents », en centimes.
 *
 * **À confirmer par Jacques**, comme l'annuel. Cinq euros de plus pour un second accès
 * plutôt que le double : le coût marginal d'un compte est presque nul, et facturer deux
 * fois le plein tarif reviendrait à faire payer la séparation deux fois. Le seuil
 * psychologique compte aussi — sous quinze euros, l'écart avec le concurrent à 19,90 €
 * reste lisible même en couvrant deux parents.
 */
export const PRIX_DUO_MENSUEL_CENTIMES = 1490;
/** Annuel duo : deux mois offerts, même règle que l'annuel individuel. */
export const PRIX_DUO_ANNUEL_CENTIMES = PRIX_DUO_MENSUEL_CENTIMES * 10;
/** Le supplément mensuel pour ouvrir l'écriture à l'autre parent. */
export const SUPPLEMENT_DUO_CENTIMES = PRIX_DUO_MENSUEL_CENTIMES - PRIX_MENSUEL_CENTIMES;
/**
 * Le même supplément, pour qui paie à l'année.
 *
 * Il existe pour que l'interface n'ait pas à le recalculer : annoncer « 5 € de plus par
 * mois » à côté d'un prix annuel serait faux d'un facteur dix, et c'est une soustraction
 * qu'on n'a pas envie de voir posée dans un composant.
 */
export const SUPPLEMENT_DUO_ANNUEL_CENTIMES = PRIX_DUO_ANNUEL_CENTIMES - PRIX_ANNUEL_CENTIMES;
/** Ce qu'on économise en payant à l'année, en centimes. */
export const ECONOMIE_ANNUELLE_CENTIMES = PRIX_MENSUEL_CENTIMES * 12 - PRIX_ANNUEL_CENTIMES;
/**
 * Offre « Early bird » — le prix de la première année pour qui s'était inscrit à la liste
 * d'attente (docs/06 §3).
 *
 * Ces deux montants étaient annoncés par la vitrine depuis l'ouverture de la liste
 * d'attente — `tarifParentEarlybirdTexte`, dans les six catalogues — sans qu'aucune ligne
 * de code sache les appliquer. C'était une promesse publique que le produit ne pouvait pas
 * tenir : l'inscrit qui aurait payé aurait payé plein tarif.
 *
 * **La première année seulement.** C'est ce que dit la vitrine, et c'est ce qui décide de
 * la forme technique : une remise ponctuelle sur le tarif annuel (`duration: once` chez
 * Stripe), jamais un second tarif. Un tarif à 79 € se reconduirait à 79 € indéfiniment, ce
 * qui ne serait pas l'offre annoncée — ce serait une autre offre, moins chère, qu'on aurait
 * faite sans le vouloir.
 *
 * L'éligibilité ne se stocke nulle part sur le compte : elle se calcule à la lecture, en
 * regardant si l'adresse figure sur la liste d'attente. Même raison que la couverture duo —
 * un droit recopié doit être défait quelque part, et la première fois qu'on l'oublie, il
 * reste ouvert.
 */
export const PRIX_EARLYBIRD_ANNUEL_CENTIMES = 7900;
/** Le même geste pour la portée « les deux parents ». */
export const PRIX_EARLYBIRD_DUO_ANNUEL_CENTIMES = 12900;
/** La remise consentie la première année, en centimes, pour chaque portée. */
export const REMISE_EARLYBIRD_CENTIMES = {
    seul: PRIX_ANNUEL_CENTIMES - PRIX_EARLYBIRD_ANNUEL_CENTIMES,
    duo: PRIX_DUO_ANNUEL_CENTIMES - PRIX_EARLYBIRD_DUO_ANNUEL_CENTIMES,
};
/** Le prix de la première année sous l'offre, pour chaque portée. */
export const PRIX_EARLYBIRD_CENTIMES = {
    seul: PRIX_EARLYBIRD_ANNUEL_CENTIMES,
    duo: PRIX_EARLYBIRD_DUO_ANNUEL_CENTIMES,
};
/**
 * Durée de l'essai gratuit ouvert à qui souscrit, en jours.
 *
 * Sept jours, comme l'annonce la vitrine (`tarifParentEssaiGratuit`) : c'est la seule
 * valeur que le produit ait jamais promise publiquement, et deux durées différentes entre
 * la page de tarifs et l'application se liraient comme une offre changée en route.
 *
 * À distinguer de l'essai **prescripteur**, ouvert par un code d'avocat ou de médiateur :
 * celui-là dure ce que dit le code (`trialCode.trialDays`), ne demande pas de carte et
 * s'obtient sans passer par Stripe. Les deux mènent au même statut `trialing` — ce sont
 * deux portes vers le même état, pas deux régimes.
 *
 * L'essai reste **facultatif et dit comme tel** : un parent peut vouloir payer tout de
 * suite, et l'imposer donnerait à sa première facture une date qu'il n'a pas choisie.
 */
export const JOURS_ESSAI_GRATUIT = 7;
/** « 9,90 € » — un montant tel qu'on l'écrit en France. */
export function prixEnEuros(centimes) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(centimes / 100);
}
//# sourceMappingURL=droits.js.map